import { useState } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Edit2, Trash2, MoreVertical, CheckSquare, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Transaction, TransactionType } from '@/hooks/useTransactions';
import { 
  formatCurrency, 
  transactionTypes,
  transactionCategories,
  paymentMethods 
} from '@/lib/foco-calculations';
import { cn } from '@/lib/utils';

interface TransactionsListProps {
  transactions: Transaction[];
  onEdit: (transaction: Transaction) => void;
  onDelete: (id: string) => void;
  onBulkUpdateCategory?: (ids: string[], category: string) => void;
  onBulkDelete?: (ids: string[]) => void;
}

// Flat list of all unique categories across all types
const allCategories = Object.values(transactionCategories)
  .flat()
  .filter((c, i, arr) => arr.findIndex(x => x.value === c.value) === i);

export function TransactionsList({ transactions, onEdit, onDelete, onBulkUpdateCategory, onBulkDelete }: TransactionsListProps) {
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkCategory, setBulkCategory] = useState<string>('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const filteredTransactions = transactions.filter(t => 
    typeFilter === 'all' || t.type === typeFilter
  );

  const isSelecting = selectedIds.size > 0;

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === filteredTransactions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredTransactions.map(t => t.id)));
    }
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setBulkCategory('');
  };

  const handleBulkApply = () => {
    if (bulkCategory && selectedIds.size > 0 && onBulkUpdateCategory) {
      onBulkUpdateCategory(Array.from(selectedIds), bulkCategory);
      clearSelection();
    }
  };

  const getTypeConfig = (type: TransactionType) => {
    const config: Record<TransactionType, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
      income: { label: 'Ingreso', variant: 'default' },
      fixed_expense: { label: 'Gasto Fijo', variant: 'secondary' },
      variable_expense: { label: 'Gasto Variable', variant: 'outline' },
      saving: { label: 'Ahorro', variant: 'default' },
      investment: { label: 'Inversión', variant: 'default' },
      debt_payment: { label: 'Deuda', variant: 'destructive' },
    };
    return config[type];
  };

  const getCategoryLabel = (type: TransactionType, category: string) => {
    const categories = transactionCategories[type] || [];
    const match = categories.find(c => c.value === category);
    if (match) return match.label;
    // Fallback: search across all category types
    return allCategories.find(c => c.value === category)?.label || category;
  };

  const getPaymentMethodLabel = (method: string) => {
    return paymentMethods.find(m => m.value === method)?.label || method;
  };

  if (transactions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-muted-foreground">No hay transacciones registradas</p>
        <p className="text-sm text-muted-foreground">
          Agrega tu primera transacción para comenzar
        </p>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
    <div className="space-y-4">
      {/* Filters + select all */}
      <div className="flex items-center gap-4 flex-wrap">
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filtrar por tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los tipos</SelectItem>
            {transactionTypes.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          variant="outline"
          size="sm"
          onClick={toggleAll}
          className="gap-2"
        >
          <CheckSquare className="h-4 w-4" />
          {selectedIds.size === filteredTransactions.length && filteredTransactions.length > 0
            ? 'Deseleccionar todo'
            : 'Seleccionar todo'}
        </Button>
      </div>

      {/* Bulk action bar */}
      {isSelecting && (
        <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/50 animate-fade-in flex-wrap">
          <span className="text-sm font-medium">
            {selectedIds.size} seleccionada{selectedIds.size > 1 ? 's' : ''}
          </span>
          <Select value={bulkCategory} onValueChange={setBulkCategory}>
            <SelectTrigger className="w-52">
              <SelectValue placeholder="Nueva categoría" />
            </SelectTrigger>
            <SelectContent>
              {allCategories.map((cat) => (
                <SelectItem key={cat.value} value={cat.value}>
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            disabled={!bulkCategory}
            onClick={handleBulkApply}
          >
            Aplicar categoría
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={() => setShowDeleteDialog(true)}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Eliminar ({selectedIds.size})
          </Button>
          <Button variant="ghost" size="sm" onClick={clearSelection}>
            <X className="h-4 w-4 mr-1" />
            Cancelar
          </Button>
        </div>
      )}

      {/* Bulk delete confirmation dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar {selectedIds.size} transacción{selectedIds.size > 1 ? 'es' : ''}?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminarán permanentemente las transacciones seleccionadas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (onBulkDelete) {
                  onBulkDelete(Array.from(selectedIds));
                }
                clearSelection();
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="rounded-lg border bg-card">
        <div className="divide-y">
          {filteredTransactions.map((transaction, index) => {
            const typeConfig = getTypeConfig(transaction.type);
            const isIncome = transaction.type === 'income';
            const isSelected = selectedIds.has(transaction.id);
            
            return (
              <div 
                key={transaction.id}
                className={cn(
                  "flex items-center justify-between p-4 hover:bg-muted/50 transition-colors animate-fade-in",
                  isSelected && "bg-primary/5"
                )}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="flex items-center gap-4">
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggleSelect(transaction.id)}
                    aria-label={`Seleccionar transacción ${getCategoryLabel(transaction.type, transaction.category)}`}
                  />
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">
                        {getCategoryLabel(transaction.type, transaction.category)}
                      </span>
                      <Badge variant={typeConfig.variant} className="text-xs">
                        {typeConfig.label}
                      </Badge>
                      {transaction.was_converted &&
                        transaction.original_amount != null &&
                        transaction.original_currency && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge variant="secondary" className="text-xs cursor-help">
                                Convertida
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs text-left">
                              <p className="font-medium">Importada con conversión</p>
                              <p className="text-xs opacity-90 mt-1">
                                Original:{' '}
                                {formatCurrency(
                                  Number(transaction.original_amount),
                                  transaction.original_currency,
                                )}
                              </p>
                              {transaction.exchange_rate_used != null && (
                                <p className="text-xs opacity-90">
                                  TC usado: {Number(transaction.exchange_rate_used)} CRC por 1 USD
                                </p>
                              )}
                              <p className="text-xs opacity-90 mt-1">
                                Guardado en {transaction.currency || 'CRC'}:{' '}
                                {formatCurrency(
                                  Number(transaction.amount),
                                  transaction.currency || 'CRC',
                                )}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>
                        {format(new Date(transaction.date), 'd MMM yyyy', { locale: es })}
                      </span>
                      <span>•</span>
                      <span>{getPaymentMethodLabel(transaction.payment_method)}</span>
                    </div>
                    {transaction.notes && (
                      <p className="text-xs text-muted-foreground line-clamp-1">
                        {transaction.notes}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <span className={cn(
                    'text-lg font-semibold',
                    isIncome ? 'text-success' : 'text-foreground'
                  )}>
                    {isIncome ? '+' : '-'}{formatCurrency(Number(transaction.amount), transaction.currency || 'CRC')}
                  </span>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onEdit(transaction)}>
                        <Edit2 className="mr-2 h-4 w-4" />
                        Editar
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => onDelete(transaction.id)}
                        className="text-destructive"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Eliminar
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
    </TooltipProvider>
  );
}
